import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  FormControl,
  InputGroup,
  ListGroup,
  Row,
} from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '@store/store'
import {
  getJobLoading,
  selectAllJobs,
} from '@editor/store/selectors/job.selector'
import {
  deleteAllJobs,
  JobModel,
  JobState,
} from '@editor/store/slices/job.slice'
import { useNavigate } from 'react-router-dom'
import {
  BsBookmarkCheckFill,
  BsExclamationDiamondFill,
  BsFileFontFill,
  BsFileMusicFill,
  BsPencilFill,
} from 'react-icons/bs'
import { useTheme } from '@config/themeContext'
import {
  fetchAllJobsFiltered,
  JOB_ORDERING_BY,
  ORDERING,
} from '@editor/store/thunks/job.thunk'
import InfiniteScroll from 'react-infinite-scroll-component'
import { selectUserInfo } from '@store/slices/auth.slice'
import {
  fetchArticleById,
  selectAllArticles,
} from '@editor/store/slices/article.slice'
import { formatDateNicelyCzech } from '../tools/time.tools'
import Loader from "@editor/components/misc/loader";

const DashboardPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch()
  const jobs = useSelector(selectAllJobs)
  const jobsLoading = useSelector(getJobLoading)
  const articles = useSelector(selectAllArticles)
  const userInfo = useSelector(selectUserInfo)
  const jobStates: JobState[] = Object.values(JobState)
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState({
    ordering: ORDERING.descending,
    orderBy: JOB_ORDERING_BY.updatedAt,
  })
  const [filterByUser, setFilterByUser] = useState(false)
  const [filterByState, setFilterByState] = useState('')

  const { theme } = useTheme()

  useEffect(() => {
    dispatch(
      fetchAllJobsFiltered({
        page,
        pageSize: 15,
        searchInTitle: search,
        order,
        filterByUser: userInfo && filterByUser ? userInfo.name : '',
        filterByState,
      })
    )
  }, [dispatch, page, search, order, filterByUser, filterByState])

  useEffect(() => {
    jobs.forEach((job) => {
      dispatch(fetchArticleById(job.article))
    })
  }, [jobs, page, search, order, filterByUser, filterByState])

  const fetchMoreData = () => {
    setPage((page) => page + 1) // Increment page number to fetch
  }

  const handleEditJobClick = (job: JobModel) => {
    navigate(`/editor/${job.id}`)
  }

  const handleNewJobClick = () => {
    navigate('/editor')
  }

  const getStateIcon = (state: JobState) => {
    switch (state) {
      case JobState.ARTICLE_STATE || JobState.SSML_STATE:
        return (
          <BsFileFontFill
            style={{
              width: '25px',
              height: '25px',
              color: theme === 'dark' ? 'lightskyblue' : 'mediumblue',
            }}
          ></BsFileFontFill>
        )
      case JobState.AUDIO_STATE:
        return (
          <BsFileMusicFill
            style={{
              width: '25px',
              height: '25px',
              color: theme === 'dark' ? 'lightskyblue' : 'mediumblue',
            }}
          ></BsFileMusicFill>
        )
      case JobState.PUBLISHED_STATE:
        return (
          <BsBookmarkCheckFill
            style={{
              width: '25px',
              height: '25px',
              color: theme === 'dark' ? 'lightgreen' : 'green',
            }}
          ></BsBookmarkCheckFill>
        )
      case JobState.ACTIONNEEDED_STATE:
        return (
          <BsExclamationDiamondFill
            style={{
              width: '25px',
              height: '25px',
              color: theme === 'dark' ? 'yellow' : 'orange',
            }}
          ></BsExclamationDiamondFill>
        )
      default:
        return ''
    }
  }
  const getStateName = (state: JobState) => {
    switch (state) {
      case JobState.ARTICLE_STATE:
        return 'Článek'
      case JobState.SSML_STATE:
        return 'SSML'
      case JobState.AUDIO_STATE:
        return 'Audio'
      case JobState.PUBLISHED_STATE:
        return 'Publikováno'
      case JobState.ACTIONNEEDED_STATE:
        return 'Potřeba opravy'
      default:
        return String(state).toLowerCase()
    }
  }
  const handleFilterChange = () => {
    dispatch(deleteAllJobs({})) // Clear jobs
    setPage(0) // Reset to first page when filters change
  }

  const handleSearchChange = (e: React.ChangeEvent) => {
    handleFilterChange()
    // @ts-ignore
    const searchValue: string = e.target.value as string
    setSearch(searchValue) // Set filters to the form control value
  }

  const handleOrderingChange = (e: React.ChangeEvent) => {
    handleFilterChange()
    // @ts-ignore
    setOrder({ ...order, orderBy: e.target.value as JOB_ORDERING_BY })
  }

  const handleOrderChange = (e: React.ChangeEvent) => {
    handleFilterChange()
    // @ts-ignore
    setOrder({ ...order, ordering: e.target.value as ORDERING })
  }

  const handleFilterByStateChange = (e: React.ChangeEvent) => {
    handleFilterChange()
    // @ts-ignore
    setFilterByState(e.target.value)
  }

  const handleFilterByUserChange = () => {
    handleFilterChange()
    setFilterByUser(!filterByUser)
  }

  const clearFilters = () => {
    setSearch('') // Clear search
    setOrder({
      ordering: ORDERING.descending,
      orderBy: JOB_ORDERING_BY.updatedAt,
    }) // Reset ordering
    setFilterByState('') // Reset state filter
    setFilterByUser(false) // Reset user filter
  }

  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  return (
    <div>
      <div className={`bg-light-subtle text-bg-${theme} shadow-sm`}>
        <Row className="app-max-width fw-bold py-3 px-2 ">
          <Col>
            <h2>Job List</h2>
          </Col>
          <Col xs={2}>
            <Button variant="success" onClick={() => handleNewJobClick()}>
              <strong>Nový článek</strong>
            </Button>
          </Col>
        </Row>
        <Row className="app-max-width">
          <InputGroup className="w-50">
            <FormControl
              placeholder="Vyhledat v titulku nebo podle ID"
              onChange={handleSearchChange}
            />
            <Button variant="secondary" onClick={clearFilters}>
              Smazat filtry
            </Button>
          </InputGroup>
        </Row>
        <Row className=" app-max-width">
          <Col className="d-flex my-3">
            <InputGroup className="me-1">
              <InputGroup.Text>Řazeno podle</InputGroup.Text>
              <FormControl
                as="select"
                value={order.orderBy}
                onChange={handleOrderingChange}
              >
                <option value={JOB_ORDERING_BY.state}>Stav</option>
                <option value={JOB_ORDERING_BY.updatedAt}>
                  Naposledy upraveno
                </option>
              </FormControl>
            </InputGroup>

            <InputGroup className="me-1 ">
              <InputGroup.Text>Řazení</InputGroup.Text>
              <FormControl
                as="select"
                value={order.ordering}
                onChange={handleOrderChange}
              >
                <option value={ORDERING.ascending}>Vzestupně</option>
                <option value={ORDERING.descending}>Sestupně</option>
              </FormControl>
            </InputGroup>
            <InputGroup className="me-1">
              <InputGroup.Text>Pouzes stav</InputGroup.Text>
              <FormControl
                as="select"
                value={filterByState}
                onChange={handleFilterByStateChange}
              >
                <option value="">Vyberte...</option>
                {jobStates.map((jobState) => (
                  <option key={jobState} value={jobState}>
                    {getStateName(jobState)}
                  </option>
                ))}
              </FormControl>
            </InputGroup>
            <InputGroup>
              <InputGroup.Text>Upraveno mnou</InputGroup.Text>
              <InputGroup.Checkbox
                checked={filterByUser}
                onChange={handleFilterByUserChange}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>
      <ListGroup className="app-max-width">
        <Row className="text-secondary px-3 pt-3 pb-1">
          <Col style={{ maxWidth: '50px' }}>ID</Col>
          <Col style={{ maxWidth: '60px' }}>ID článku</Col>
          <Col style={{ maxWidth: '50px' }} className="ms-2"></Col>
          <Col xs={5}>Název</Col>
          <Col style={{ minWidth: '145px' }}>Doména</Col>
          <Col>Upravil</Col>
          <Col xs={2}>Naposledy upraveno</Col>
          <Col></Col>
        </Row>
        <Row>
          <InfiniteScroll
            dataLength={jobs.length} // length of the data
            next={fetchMoreData} // function to fetch more data
            hasMore={true} // indicates if there's more data to load
            loader={<Loader loading={jobsLoading} />}
            height={750}
          >
            <Loader loading={jobsLoading} />
            {!jobs.length && !jobsLoading && (
              <h4 className="mx-auto mt-5 w-25 text-center text-secondary">
                Žádné výsledky
              </h4>
            )}
            {jobs.map((job) => {
              const article = articles.find(
                (article) => article.id === job.article
              )
              return (
                <ListGroup.Item key={job.id} variant="light">
                  <Row>
                    <Col
                      className="text-secondary"
                      style={{ maxWidth: '50px', textAlign: 'center' }}
                    >
                      {job.id}
                    </Col>
                    <Col
                      className="text-secondary"
                      style={{ maxWidth: '60px', textAlign: 'center' }}
                    >
                      {article?.articleId}
                    </Col>
                    <Col style={{ maxWidth: '50px' }} className="ms-2">
                      {getStateIcon(job.state)}
                    </Col>
                    <Col
                      xs={5}
                      className="fw-bold"
                      style={{
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      {job.name}
                    </Col>
                    <Col style={{ minWidth: '145px' }}>
                      {article
                        ? new URL(article.url).hostname.replace(/^www\./, '')
                        : 'Nepodařilo se rozpoznat doménu'}
                    </Col>
                    <Col>{job.updatedBy}</Col>
                    <Col xs={2}>
                      {formatDateNicelyCzech(new Date(job.updatedAt))}
                    </Col>
                    <Col>
                      <Button
                        variant="primary"
                        onClick={() => handleEditJobClick(job)}
                      >
                        <BsPencilFill></BsPencilFill>&nbsp;
                        <strong>Edit</strong>
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              )
            })}
          </InfiniteScroll>
        </Row>
      </ListGroup>
    </div>
  )
}

export default DashboardPage
